import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { bookReleaseAPI, publisherAPI } from "../api/api";
import { Publisher } from "../types/book";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "GMT", label: "Greenwich Mean Time (GMT)" },
];

const BookReleaseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    publisher: "",
    releaseDate: new Date(),
    description: "",
    coverImage: "",
    price: "",
    currency: "USD",
    preOrderLink: "",
    editionType: "Standard",
    status: "Upcoming",
    featured: false,
  });
  const [selectedTimezone, setSelectedTimezone] = useState(TIMEZONES[0].value);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(
    null,
  );
  const isEditing = Boolean(id);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const publishersResponse = await publisherAPI.getPublishers();
        setPublishers(publishersResponse);

        if (isEditing && id) {
          const releaseData = await bookReleaseAPI.getBookRelease(id);
          const publisher = publishersResponse.find(
            (p: Publisher) => p._id === releaseData.publisher._id,
          );
          setSelectedPublisher(publisher || null);

          // Convert UTC date to local timezone for display
          const localDate = utcToZonedTime(
            new Date(releaseData.releaseDate),
            selectedTimezone,
          );

          setFormData({
            title: releaseData.title,
            author: releaseData.author,
            publisher: releaseData.publisher._id,
            releaseDate: localDate,
            description: releaseData.description,
            coverImage: releaseData.coverImage || "",
            price: releaseData.price?.toString() || "",
            currency: releaseData.currency || "USD",
            preOrderLink: releaseData.preOrderLink || "",
            editionType: releaseData.editionType,
            status: releaseData.status,
            featured: releaseData.featured || false,
          });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      }
    };
    fetchData();
  }, [id, isEditing, selectedTimezone]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePublisherChange = (
    event: React.SyntheticEvent,
    newValue: Publisher | null,
  ) => {
    console.log("Selected publisher:", newValue);
    setSelectedPublisher(newValue);
    setFormData((prev) => ({
      ...prev,
      publisher: newValue?._id || "",
    }));
  };

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      currency: event.target.value,
    }));
  };

  const handleEditionTypeChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      editionType: event.target.value,
    }));
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.value,
    }));
  };

  const handleDateTimeChange = (date: Date | null) => {
    if (date) {
      setFormData({
        ...formData,
        releaseDate: date,
      });
    }
  };

  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    const newTimezone = event.target.value;
    setSelectedTimezone(newTimezone);

    // Convert the current date to the new timezone
    const currentDate = formData.releaseDate;
    const newDate = utcToZonedTime(
      zonedTimeToUtc(currentDate, selectedTimezone),
      newTimezone,
    );

    setFormData((prev) => ({
      ...prev,
      releaseDate: newDate,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (
      !formData.title ||
      !formData.author ||
      !formData.publisher ||
      !formData.description
    ) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    try {
      // Convert the date to UTC before sending to the server
      const utcDate = zonedTimeToUtc(formData.releaseDate, selectedTimezone);

      const bookReleaseData = {
        ...formData,
        releaseDate: utcDate,
        price: formData.price ? parseFloat(formData.price) : undefined,
        publisher: formData.publisher || selectedPublisher?._id,
      };

      let releaseId = id;
      if (!isEditing) {
        const newRelease =
          await bookReleaseAPI.createBookRelease(bookReleaseData);
        releaseId = newRelease._id;
      } else {
        await bookReleaseAPI.updateBookRelease(id!, bookReleaseData);
      }
      navigate(`/releases/${releaseId}`);
    } catch (err: any) {
      console.error("Error saving book release:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while saving the book release",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            {isEditing ? "Edit Book Release" : "Create New Book Release"}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label="Book Title"
              name="title"
              autoFocus
              value={formData.title}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="author"
              label="Author"
              name="author"
              value={formData.author}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="normal">
              <Autocomplete
                id="publisher"
                options={publishers}
                getOptionLabel={(option) => option.name}
                value={selectedPublisher}
                onChange={handlePublisherChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Publisher"
                    required
                    inputProps={{
                      ...params.inputProps,
                      "aria-label": "Publisher",
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
              />
            </FormControl>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Release Date & Time"
                  value={formData.releaseDate}
                  onChange={handleDateTimeChange}
                  sx={{ flex: 2 }}
                />
              </LocalizationProvider>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="timezone-label">Timezone</InputLabel>
                <Select
                  labelId="timezone-label"
                  id="timezone"
                  value={selectedTimezone}
                  label="Timezone"
                  onChange={handleTimezoneChange}
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              margin="normal"
              required
              fullWidth
              multiline
              rows={4}
              id="description"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!formData.description && formData.description !== ""}
              helperText={
                !formData.description && formData.description !== ""
                  ? "Description is required"
                  : ""
              }
            />
            <TextField
              margin="normal"
              fullWidth
              id="coverImage"
              label="Cover Image URL"
              name="coverImage"
              value={formData.coverImage}
              onChange={handleChange}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                margin="normal"
                fullWidth
                id="price"
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="currency-label">Currency</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  label="Currency"
                  onChange={handleCurrencyChange}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              margin="normal"
              fullWidth
              id="preOrderLink"
              label="Pre-order Link"
              name="preOrderLink"
              value={formData.preOrderLink}
              onChange={handleChange}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="editionType-label">Edition Type</InputLabel>
                <Select
                  labelId="editionType-label"
                  id="editionType"
                  name="editionType"
                  value={formData.editionType}
                  label="Edition Type"
                  onChange={handleEditionTypeChange}
                >
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Special">Special</MenuItem>
                  <MenuItem value="Limited">Limited</MenuItem>
                  <MenuItem value="Collector">Collector</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="Upcoming">Upcoming</MenuItem>
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Sold Out">Sold Out</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.featured}
                  onChange={handleCheckboxChange}
                  name="featured"
                  color="primary"
                />
              }
              label="Feature this release"
              sx={{ mt: 2 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isEditing ? (
                "Update Book Release"
              ) : (
                "Create Book Release"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default BookReleaseForm;
